import {
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect,
  KeyboardEvent,
} from "react";
import { tabbable } from "tabbable";
import { MultiSelectFilter, SingleSelectFilter } from "./internal/types";
import Listbox from "../../../../components/Listbox";
import ListView from "../../../../components/ListView";
import { SearchField } from "../../../../components/SearchField";

import styles from "./Filters.module.scss";

/**
 * Props for the FilterSelect component
 */
interface FilterSelectProps<Item extends { id: string; label: string }> {
  /** The filter configuration object */
  filter: SingleSelectFilter<Item> | MultiSelectFilter<Item>;
  /** The current draft value for the filter */
  draftValue: Item | Item[] | undefined;
  /** Callback to update the draft value */
  onDraftChange: (value: Item | Item[] | undefined) => void;
}

/**
 * FilterSelect component renders a Listbox or ListView for single or multi-select filters.
 * Built into FilterButton and FilterDrawer for filter selection UI.
 *
 * Features:
 * - Renders a Listbox for single select filters
 * - Renders a ListView for multi select filters
 * - Supports search field for filtering options
 */
export const FilterSelect = <Item extends { id: string; label: string }>({
  filter,
  draftValue,
  onDraftChange,
}: FilterSelectProps<Item>) => {
  const isMultiSelect = filter.type === "multiSelect";

  // Ref to track the list container for focus management
  const listRef = useRef<HTMLDivElement>(null);

  // For multi-select with search: keep a stable snapshot of items so that a selection
  // (which causes the consumer to re-render and potentially reset filter.items) does not
  // clear the search-filtered list while the search field still has a value.
  const shouldStabilizeItems = isMultiSelect && filter.hasSearch;
  const [stableItems, setStableItems] = useState(filter.items);
  const isSearchActiveRef = useRef(false);
  const expectingSearchUpdate = useRef(false);

  // displayItems is filter.items for all non-stabilized cases so items always stay
  // in sync with props without an extra render cycle via state.
  const displayItems = shouldStabilizeItems ? stableItems : filter.items;

  useEffect(() => {
    if (!shouldStabilizeItems) return;
    if (expectingSearchUpdate.current) {
      // This items update was triggered by a new search term — accept it
      setStableItems(filter.items);
      expectingSearchUpdate.current = false;
    } else if (!isSearchActiveRef.current) {
      // Search is not active — freely sync items (e.g. search cleared, initial load)
      setStableItems(filter.items);
    }
    // While search is active and this isn't a search-triggered update (i.e. a selection
    // update caused the consumer to re-render), preserve the current stableItems.
  }, [filter.items, shouldStabilizeItems]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      filter.onSearch?.(value);
      if (shouldStabilizeItems) {
        if (value.length > 0) {
          isSearchActiveRef.current = true;
          expectingSearchUpdate.current = true;
        } else {
          isSearchActiveRef.current = false;
        }
      }
    },
    [filter, shouldStabilizeItems],
  );

  const handleSearchClear = useCallback(() => {
    filter.onSearchClear?.();
    if (shouldStabilizeItems) {
      isSearchActiveRef.current = false;
    }
  }, [filter, shouldStabilizeItems]);

  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown" && listRef.current) {
        e.preventDefault();
        // Find first focusable element in the list and focus it
        const focusableElements = tabbable(listRef.current);
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    },
    [],
  );

  const singleSelectList = useMemo(
    () => (
      <div ref={listRef}>
        <Listbox
          key={filter.id}
          items={filter.items}
          selected={draftValue as Item | undefined}
          onSelectionChange={(selected: Item | undefined) => {
            onDraftChange(selected);
          }}
          disableAutoSelectOnFocus
          style={{ paddingInline: "1rem", paddingBlockStart: "1rem" }}
        >
          {({ items }) =>
            items.map((item) => (
              <Listbox.Option
                key={item.id}
                item={item}
                disabled={item.disabled}
              >
                {item.label}
              </Listbox.Option>
            ))
          }
        </Listbox>
      </div>
    ),
    [filter.id, filter.items, draftValue, onDraftChange],
  );

  const multiSelectList = useMemo(
    () => (
      <div ref={listRef}>
        <ListView
          key={filter.id}
          items={displayItems}
          selected={Array.isArray(draftValue) ? draftValue : []}
          onSelectionChange={(selectedItems: unknown[]) => {
            const items = selectedItems as Item[];
            onDraftChange(items);
          }}
          style={{ paddingInline: "1rem", paddingBlockStart: "1rem" }}
        >
          {({ items }) =>
            items.map((item) => (
              <ListView.Option key={item.id} item={item}>
                {item.label}
              </ListView.Option>
            ))
          }
        </ListView>
      </div>
    ),
    [filter.id, displayItems, draftValue, onDraftChange],
  );

  return (
    <>
      {filter.hasSearch && (
        <SearchField
          size="small"
          className={styles["filter-select-search"]}
          onChange={handleSearch}
          onClear={handleSearchClear}
          onKeyDown={handleSearchKeyDown}
          data-anv="filter-select-search"
        />
      )}
      {isMultiSelect ? multiSelectList : singleSelectList}
    </>
  );
};
